/**
 * The starfield (docs/spec/04 §2): coloured stars drifting down at two speeds, each blinking on
 * its own beat, as the 1981 board's did. Enhanced adds a third, slow, dim layer and the odd streak.
 * Its randomness is its own: nothing here touches the game.
 */

import { CYAN, LIME, MAGENTA, ORANGE, PINK, RED, SKY, WHITE, YELLOW } from './palette';
import type { Screen } from './screen';
import { SCREEN_H, SCREEN_W } from './screen';

interface Star {
  x: number;
  y: number;
  /** 0 the deep layer, 1 and 2 the board's two. */
  layer: number;
  colour: number;
  /** Blink period and offset, ms. */
  period: number;
  phase: number;
}

const COLOURS = [WHITE, RED, YELLOW, CYAN, LIME, SKY, MAGENTA, ORANGE, PINK];
/** Pixels a second, by layer. */
const SPEED = [9, 26, 52];
const COUNT = [46, 44, 34];
const DIM = ['#2a3150', '', ''];

export class Starfield {
  private readonly stars: Star[] = [];
  private seed: number;
  private ms = 0;
  private streak: { x: number; y: number; life: number } | null = null;

  constructor(seed = 7) {
    this.seed = seed >>> 0;
    for (let layer = 0; layer < 3; layer++) {
      for (let i = 0; i < (COUNT[layer] ?? 0); i++) {
        this.stars.push({
          x: Math.floor(this.rand() * SCREEN_W),
          y: this.rand() * SCREEN_H,
          layer,
          colour: COLOURS[Math.floor(this.rand() * COLOURS.length)] ?? WHITE,
          period: 500 + this.rand() * 1400,
          phase: this.rand() * 2000,
        });
      }
    }
  }

  private rand(): number {
    this.seed = (Math.imul(this.seed, 1664525) + 1013904223) >>> 0;
    return this.seed / 4294967296;
  }

  /** Move the stars on. `warp` multiplies their speed (stage intros run fast). */
  update(dtMs: number, warp = 1): void {
    this.ms += dtMs;
    for (const s of this.stars) {
      s.y += ((SPEED[s.layer] ?? 0) * warp * dtMs) / 1000;
      if (s.y >= SCREEN_H) {
        s.y -= SCREEN_H;
        s.x = Math.floor(this.rand() * SCREEN_W);
      }
    }
    if (this.streak) {
      this.streak.life -= dtMs;
      this.streak.x -= dtMs * 0.22;
      this.streak.y += dtMs * 0.3;
      if (this.streak.life <= 0) this.streak = null;
    } else if (this.rand() < dtMs / 9000) {
      this.streak = { x: 40 + this.rand() * 180, y: 20 + this.rand() * 120, life: 260 };
    }
  }

  /** `deep`: the Enhanced layer and streaks. `calm`: stars do not blink (Reduce flashing). */
  draw(screen: Screen, deep: boolean, calm = false): void {
    for (const s of this.stars) {
      if (s.layer === 0 && !deep) continue;
      if (!calm && s.layer > 0 && (this.ms + s.phase) % s.period < s.period * 0.32) continue;
      screen.fill(s.x, Math.floor(s.y), 1, 1, s.layer === 0 ? (DIM[0] ?? WHITE) : s.colour);
    }
    const k = this.streak;
    if (deep && k) {
      for (let i = 0; i < 6; i++) screen.fill(k.x + i * 1.5, k.y - i * 2, 1, 1, i < 2 ? WHITE : '#5a6a9a');
    }
  }
}
